import { BorderGridContainer, Clickable, GridBox, H2, H3, P, Section, Wrapper } from '@ahmdigital/ui';
import { head } from 'lodash/fp';
import moment from 'moment';

import { GROWTHBOOK_ADD_ONS_PAGE } from '../../../../ahm-constants/ui';
import { ProductOption } from '../../../../types/__codegen__/sanity/graphql';
import { useFeatureIsOn } from '../../../../../../utils/growth-book/hooks';
import Banner from '../../../../elements/banner';
import config from '../../../../config';
import constants from '../../../../ahm-constants';
import Head from '../../../../../utils/head';
import mapTermsAndConditions from '../../../../utils/map-terms-and-conditions';
import styles from './styles.module.sass';
import TermsAndConditions from '../../../insurance/components/terms-and-conditions';

type UsefulDocumentsPageProps = {
  content: ProductOption | null;
};

const STATIC_DOMAIN = config.get('static:url');

const PDS_LINK = `${STATIC_DOMAIN}/files/cash-boost/pds-june-2026.pdf`;
const TMD_LINK = `${STATIC_DOMAIN}/files/cash-boost/tmd-june-2026.pdf`;
const FSG_LINK = `${STATIC_DOMAIN}/files/cash-boost/fsg-june-2026.pdf`;
const EFFECTIVE_DATE = '22/06/2026';

const UsefulDocumentsPage = ({ content }: UsefulDocumentsPageProps) => {
  const isFeatureEnabled = useFeatureIsOn(GROWTHBOOK_ADD_ONS_PAGE);

  if (!isFeatureEnabled) {
    return null;
  }

  return (
    <>
      <Head titleTemplate="%s - ahm">
        <title>PDS and Useful Documents - ahm Cash Boost</title>
        <meta name="description" content="Need help? Get in touch with us about ahm Cash Boost." />
      </Head>
      <BorderGridContainer hideBorderTop>
        <Banner preHeading="ahm Cash boost" title="Useful Documents" />
        <GridBox padding="0">
          <Section>
            <Wrapper className={styles.wrapper}>
              <H2 className={styles.heading} looksLike="h4">
                Cash Boost policies purchased on and from{' '}
                {moment(EFFECTIVE_DATE, constants.DATE_FORMAT.STANDARD).format(constants.DATE_FORMAT.HUMAN)}.
              </H2>
              <Section variant={['paddingTopSpacerNone', 'paddingBottomSpacerNone']}>
                <H3 looksLike="h4" variant="marginBottomSpacer10">
                  Product Disclosure Statement
                </H3>
                <P>
                  The Product Disclosure Statement provides detail on the key features, benefits, exclusions, terms and
                  conditions of the relevant product.
                </P>
                <Clickable to={PDS_LINK}>
                  ahm Cash Boost Product Disclosure Statement (effective {EFFECTIVE_DATE})
                </Clickable>
              </Section>

              <Section variant="paddingBottomSpacerNone">
                <H3 looksLike="h4" variant="marginBottomSpacer10">
                  Target Market Determination
                </H3>
                <Clickable to={TMD_LINK}>
                  ahm Cash Boost Target Market Determination (effective {EFFECTIVE_DATE})
                </Clickable>
              </Section>

              <Section variant={['paddingBottomSpacerNone']}>
                <H3 looksLike="h4" variant="marginBottomSpacer10">
                  Financial Services Guide
                </H3>
                <P>
                  The Financial Services Guide contains information about who is providing you with the financial
                  services. It explains the financial service offered, the fees charged and how we will deal with
                  complaints.
                </P>
                <Clickable to={FSG_LINK}>
                  ahm Cash Boost Financial Services Guide (effective {EFFECTIVE_DATE})
                </Clickable>
              </Section>

              <Section variant={['paddingBottomSpacerNone']}>
                <H3 looksLike="h4" variant="marginBottomSpacer10">
                  Life Insurance Code of Practice
                </H3>
                <P>
                  The Life Insurance Code of Practice is the life insurance industry’s commitment to customer service
                  standards and it’s designed to protect you, the customer. To learn more,{' '}
                  <Clickable to={constants.URLS.THIRD_PARTY.CASH_BOOST_CODE_OF_PRACTICE}>click here</Clickable>.
                </P>
              </Section>

              <Section variant="paddingBottomSpacerNone">
                <H3 looksLike="h4" variant="marginBottomSpacer10">
                  Need to make a claim?
                </H3>
                <P>
                  Learn more on <Clickable to={constants.URLS.SALES.CASH_BOOST_HOW_TO_CLAIM}>how to claim</Clickable>
                </P>
              </Section>

              <Section variant="paddingBottomSpacerNone">
                <H3 looksLike="h4" variant="marginBottomSpacer10">
                  Need Help?
                </H3>
                <P>Call us on {constants.UI.CONTACT.PHONE.PRETTY} between 8am - 8pm Monday to Friday (AEDT).</P>
              </Section>

              <Section variant="paddingBottomSpacerNone">
                <H3 looksLike="h4" variant="marginBottomSpacer10">
                  Complaints
                </H3>
                <P>
                  If you have a query or complaint about your policy, you can contact us through{' '}
                  {constants.UI.CONTACT.PHONE.PRETTY}. We will acknowledge your complaint and endeavor to resolve your
                  complaint.
                </P>
              </Section>
            </Wrapper>
          </Section>
        </GridBox>
      </BorderGridContainer>
      {content?.termsAndConditions && (
        <TermsAndConditions terms={mapTermsAndConditions(head(content.termsAndConditions))} />
      )}
    </>
  );
};

export default UsefulDocumentsPage;
